/**
 * Composable for accessing tenant configuration
 *
 * Provides reactive access to the current tenant's configuration.
 */

import { computed, type ComputedRef } from 'vue'
import { getConfig, getCurrentTenantId, usesLarxDataSource } from '@/config'
import type { TenantConfig, TenantId } from '@/config/types'

interface UseTenantConfigReturn {
  /** The full tenant configuration */
  config: TenantConfig
  /** The current tenant ID */
  tenantId: TenantId
  /** Whether this tenant uses LARX as data source */
  isLarxTenant: ComputedRef<boolean>
  /** Whether this tenant uses Arizona S3 as data source */
  isArizonaTenant: ComputedRef<boolean>
}

/**
 * Access the current tenant configuration
 *
 * @example
 * ```ts
 * const { config, tenantId, isLarxTenant } = useTenantConfig()
 *
 * // Access branding
 * const logo = config.branding.logo
 *
 * // Check data source
 * if (isLarxTenant.value) {
 *   // Use LARX API
 * }
 * ```
 */
export function useTenantConfig(): UseTenantConfigReturn {
  const config = getConfig()
  const tenantId = getCurrentTenantId()

  const isLarxTenant = computed(() => usesLarxDataSource())
  const isArizonaTenant = computed(() => config.dataSource === 'arizona-s3')

  return {
    config,
    tenantId,
    isLarxTenant,
    isArizonaTenant,
  }
}
